// @ts-check
// Notas fiscais (NFS-e) emitidas pelo Asaas no painel de suporte: filtro por situação, tabela com o dono, número,
// data e valor, links do PDF e do XML, e as ações de emitir uma nota agendada ou cancelar. Também usada na aba
// Notas fiscais da instituição.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { searchParamsState, updateSearchParams } from '../../../app/router.js';
import { authorizeAdminInvoice, cancelAdminInvoice, fetchAdminInvoices } from '../../../data/admin-finance.js';
import { Button } from '../../../ui/button.js';
import { Modal } from '../../../ui/modal.js';
import { showSuccessSnackbar } from '../../../ui/snackbar.js';
import { showOperationsError } from '../operations-format.js';
import { AdminEmpty, AdminLoading, AdminPage, ExternalLink, FilterField, FilterSelect, InvoiceStatusTag, OwnerLink, ProviderNotice, formatDay, formatInteger, formatMoney } from './admin-ui.js';
import { t } from '../../../i18n/index.js';

const STATUS_FILTERS = ['ALL', 'SCHEDULED', 'AUTHORIZED', 'PROCESSING_CANCELLATION', 'CANCELED', 'CANCELLATION_DENIED', 'ERROR'];

/** @param {string} key @param {Record<string, unknown>} [values] */
const text = (key, values) => t(`operations.admin.panel.invoices.${key}`, values);

/**
 * @param {{ invoices: any[], showOwner?: boolean, onAuthorize: (invoice: any) => void, onCancel: (invoice: any) => void, busyId?: string | null }} props
 */
export function InvoicesTable({ invoices, showOwner = true, onAuthorize, onCancel, busyId = null }) {
  return html`<div class="settings-table admin-table">
    <div class="settings-table-row settings-table-header admin-invoice-row" data-owner=${showOwner ? 'true' : 'false'}>
      ${showOwner ? html`<span>${text('owner')}</span>` : null}
      <span>${text('number')}</span><span>${text('service')}</span><span>${text('effectiveDate')}</span>
      <span class="settings-table-number">${text('value')}</span><span>${text('status')}</span><span></span>
    </div>
    ${invoices.map((invoice) => html`<div key=${invoice.id} class="settings-table-row admin-invoice-row" data-owner=${showOwner ? 'true' : 'false'} title=${invoice.statusDescription ?? ''}>
      ${showOwner ? html`<span class="admin-cell-stack"><${OwnerLink} owner=${invoice.owner} /></span>` : null}
      <span class="admin-cell-stack"><span>${invoice.number ? text('numbered', { number: invoice.number }) : text('noNumber')}</span><span class="settings-table-muted settings-table-mono">${invoice.id}</span></span>
      <span class="admin-cell-stack"><span class="admin-cell-ellipsis">${invoice.serviceDescription || '-'}</span><span class="settings-table-muted admin-cell-ellipsis">${invoice.municipalServiceName || ''}</span></span>
      <span class="settings-table-muted">${formatDay(invoice.effectiveDate)}</span>
      <span class="settings-table-number admin-cell-money">${formatMoney(invoice.value)}</span>
      <span><${InvoiceStatusTag} status=${invoice.status} /></span>
      <span class="admin-row-actions">
        <${ExternalLink} href=${invoice.pdfUrl} label="PDF" icon="IconDownload" />
        <${ExternalLink} href=${invoice.xmlUrl} label="XML" icon="IconDownload" />
        ${invoice.status === 'SCHEDULED' ? html`<${Button} title=${text('authorize')} icon="IconSend" variant="secondary" size="small" isLoading=${busyId === invoice.id} onClick=${() => onAuthorize(invoice)} />` : null}
        ${['SCHEDULED', 'AUTHORIZED', 'CANCELLATION_DENIED'].includes(invoice.status) ? html`<${Button} title=${text('cancel')} icon="IconX" variant="secondary" accent="danger" size="small" disabled=${busyId === invoice.id} onClick=${() => onCancel(invoice)} />` : null}
      </span>
    </div>`)}
  </div>`;
}

/**
 * @param {{ tenantId?: string, showOwner?: boolean, embedded?: boolean, refreshKey?: number }} props
 */
export function InvoicesList({ tenantId, showOwner = true, embedded = false, refreshKey = 0 }) {
  const parameters = searchParamsState.value;
  const prefix = embedded ? 'inv_' : '';
  const status = STATUS_FILTERS.includes(parameters.get(`${prefix}status`) ?? '') ? /** @type {string} */ (parameters.get(`${prefix}status`)) : 'ALL';
  const [page, setPage] = useState(/** @type {any} */ (null));
  const [offset, setOffset] = useState(0);
  const [version, setVersion] = useState(0);
  const [busyId, setBusyId] = useState(/** @type {string | null} */ (null));
  const [canceling, setCanceling] = useState(/** @type {any} */ (null));

  useEffect(() => {
    setPage(null);
    fetchAdminInvoices({ status: status === 'ALL' ? undefined : status, tenantId, offset }).then(setPage).catch(showOperationsError);
  }, [status, tenantId, offset, version, refreshKey]);

  const authorize = async (/** @type {any} */ invoice) => {
    setBusyId(invoice.id);
    try {
      await authorizeAdminInvoice(invoice.id);
      showSuccessSnackbar(text('authorized'));
      setVersion(version + 1);
    } catch (error) {
      showOperationsError(error);
    } finally {
      setBusyId(null);
    }
  };
  const cancel = async () => {
    if (!canceling) return;
    setBusyId(canceling.id);
    try {
      await cancelAdminInvoice(canceling.id);
      showSuccessSnackbar(text('canceled'));
      setCanceling(null);
      setVersion(version + 1);
    } catch (error) {
      showOperationsError(error);
    } finally {
      setBusyId(null);
    }
  };

  return html`<div class="admin-list-block">
    <div class="admin-toolbar">
      <${FilterField} label=${text('status')}>
        <${FilterSelect} value=${status} ariaLabel=${text('status')} onChange=${(/** @type {string} */ value) => {
          setOffset(0);
          updateSearchParams({ [`${prefix}status`]: value === 'ALL' ? null : value });
        }} options=${STATUS_FILTERS.map((value) => ({ value, label: value === 'ALL' ? text('allStatuses') : t(`operations.admin.panel.invoiceStatuses.${value}`) }))} />
      </${FilterField}>
    </div>
    ${page === null ? html`<${AdminLoading} rows=${4} />` : html`
      <${ProviderNotice} isProviderConfigured=${page.isProviderConfigured} />
      ${page.invoices.length === 0
        ? html`<${AdminEmpty} icon="IconFileText" title=${text('empty')} subtitle=${text('emptyHint')} />`
        : html`<${InvoicesTable} invoices=${page.invoices} showOwner=${showOwner} busyId=${busyId} onAuthorize=${authorize} onCancel=${setCanceling} />
          <div class="admin-pagination">
            <span class="settings-table-muted">${text('pageInfo', { from: page.offset + 1, to: page.offset + page.invoices.length, total: formatInteger(page.totalCount) })}</span>
            <div class="admin-pagination-actions">
              <${Button} title=${t('operations.admin.panel.payments.previousPage')} variant="secondary" size="small" disabled=${page.offset === 0} onClick=${() => setOffset(Math.max(0, page.offset - page.limit))} />
              <${Button} title=${t('operations.admin.panel.payments.nextPage')} variant="secondary" size="small" disabled=${!page.hasMore} onClick=${() => setOffset(page.offset + page.limit)} />
            </div>
          </div>`}
    `}
    ${canceling ? html`<${Modal} title=${text('cancelTitle')} subtitle=${text('cancelSubtitle')} confirmLabel=${text('cancelConfirm')} confirmAccent="danger"
      isLoading=${busyId === canceling.id} onClose=${() => setCanceling(null)} onConfirm=${cancel} />` : null}
  </div>`;
}

export function AdminInvoicesPage() {
  return html`<${AdminPage} icon="IconFileText" title=${t('operations.admin.panel.sections.invoices')} subtitle=${text('subtitle')} wide=${true}>
    <${InvoicesList} />
  </${AdminPage}>`;
}
